import { useState } from 'react';
import { get } from 'lodash';
import { Button, ButtonGroup } from '@lendi-ui/button';
import Modal from '@lendi-ui/modal';
import { RadioGroup } from '@lendi-ui/radio';
import { Body } from '@lendi-ui/typography';

import { FIELD_PATHS } from '@funnels/constants';
import { Company, RelatedParty, RelatedPartyEntityIndex, StoreEmployment } from '@funnels/types';

import { Link } from '../../style';

interface AccountantCopierProps extends RelatedPartyEntityIndex {
  employments: StoreEmployment[];
  companies: Company[];
  relatedParties: RelatedParty[];
  copyAccountant: (fromIndex: number) => void;
  size?: 'lg' | 'md' | 'sm' | 'xs' | 'xxs';
}

function notEmpty<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}

export const AccountantCopier = ({
  employments,
  companies,
  relatedParties,
  relatedPartyIndex,
  copyAccountant,
  size = 'xs',
}: AccountantCopierProps) => {
  const [selectedRelatedPartyIndex, setSelectedRelatedPartyIndex] = useState<string | null>(null);
  const [modalVisibility, setModalVisibility] = useState(false);

  return (
    <>
      <Link
        size={size}
        onClick={() => {
          setModalVisibility(!modalVisibility);
        }}
      >
        Copy details from another business
      </Link>
      <Modal
        role="dialog"
        isVisible={modalVisibility}
        size="lg"
        isHeaderFixed
        onHide={() => {
          setSelectedRelatedPartyIndex(null);
          setModalVisibility(!modalVisibility);
        }}
      >
        <Modal.Header title="Copy details from another business" />
        <Modal.Content>
          <Body size="md">Select from one of the following business to copy the accountant details</Body>
          <RadioGroup
            onChange={(e) => setSelectedRelatedPartyIndex(e.target.value)}
            selectedValue={selectedRelatedPartyIndex?.toString() || ''}
          >
            {relatedParties
              .map((_, index) => {
                // loop through related parties and if the related party is about accountant for access,
                // and get the accountant with the associated employment id
                const employmentId = get({ relatedParties }, FIELD_PATHS.RELATED_PARTY_EMPLOYMENT_ID(index));
                const title = get({ relatedParties }, FIELD_PATHS.RELATED_PARTY_TITLE(index));
                const firstName = get({ relatedParties }, FIELD_PATHS.RELATED_PARTY_FIRST_NAME(index));
                const lastName = get({ relatedParties }, FIELD_PATHS.RELATED_PARTY_LAST_NAME(index));
                const phone = get({ relatedParties }, FIELD_PATHS.RELATED_PARTY_PHONE(index));
                const businessName = get({ relatedParties }, FIELD_PATHS.RELATED_PARTY_BUSINESS_NAME(index));
                const address = get({ relatedParties }, FIELD_PATHS.RELATED_PARTY_FORMATTED_ADDRESS(index));
                const hasCopyValues = title || firstName || lastName || phone || businessName || address;
                if (!!employmentId && index !== relatedPartyIndex && !!hasCopyValues) {
                  const employmentIndex = employments.findIndex((e: StoreEmployment) => e.id === employmentId);
                  const companyClientId = get(
                    { employments },
                    FIELD_PATHS.EMPLOYMENT_COMPANY_CLIENT_ID(employmentIndex)
                  );

                  const relatedCompanyIndex = companies.findIndex(
                    (company) => get({ companies: [company] }, FIELD_PATHS.COMPANY_CLIENT_ID(0)) === companyClientId
                  );

                  const companyName = get({ companies }, FIELD_PATHS.COMPANY_BUSINESS_NAME(relatedCompanyIndex));

                  return <RadioGroup.Radio key={employmentId} label={companyName} value={index.toString()} />;
                }
                return null;
              })
              .filter(notEmpty)}
          </RadioGroup>
        </Modal.Content>
        <Modal.Footer>
          <ButtonGroup isFullWidth size="md">
            <Button
              variant="primary"
              isDisabled={!selectedRelatedPartyIndex}
              isFullWidth
              onClick={() => {
                copyAccountant(+selectedRelatedPartyIndex!);
                setModalVisibility(!modalVisibility);
              }}
            >
              Done
            </Button>
            <Button variant="secondary" isFullWidth onClick={() => setModalVisibility(!modalVisibility)}>
              Cancel
            </Button>
          </ButtonGroup>
        </Modal.Footer>
      </Modal>
    </>
  );
};
